/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import {
  ALARM_SEVERITIES,
  getAlarmSeverityColor,
  normalizeAlarmSeverity,
} from "../../lib/AlarmColors";

const alarmLevel = (alarm) => normalizeAlarmSeverity(alarm.severity);

/** Worst severity with a positive count, or undefined when none. */
const worstSeverity = (alarms) =>
  ALARM_SEVERITIES.find((severity) =>
    alarms?.some((alarm) => alarmLevel(alarm) === severity && alarm.count > 0),
  );

const count = (alarms) => {
  const worst = worstSeverity(alarms);
  if (!worst) return 0;
  return alarms.find((alarm) => alarmLevel(alarm) === worst)?.count || 0;
};

const sum = (alarms) => {
  return 0;
};

const color = (alarms) => worstSeverity(alarms) ?? "grey";

const AlarmIndicator = ({ alarms, showSum }) => {
  const theme = useTheme();
  const worst = worstSeverity(alarms);
  let alarmCount = 0;
  if (!showSum) {
    alarmCount = count(alarms);
  } else {
    alarmCount = sum(alarms);
  }
  const backgroundColor = worst ? getAlarmSeverityColor(worst) : "#EEEEEE";
  const numberColor = theme.palette.getContrastText(backgroundColor);
  return (
    <div role={"alarm-" + color(alarms)} className="alarm" style={{ backgroundColor }}>
      <span role="alarm" className="number" style={{ color: numberColor }}>
        {alarmCount}
      </span>
    </div>
  );
};

AlarmIndicator.propTypes = {
  alarms: PropTypes.array,
  showSum: PropTypes.bool,
};

export { AlarmIndicator, count, color, worstSeverity };
